import React, { useEffect, useState } from "react";
import { api } from "../../../software/api";
import { useMailShake, useMailCounts } from "../mail/useMailShake";
import { useAuth } from "../../../software/AuthContext";
import { motion } from "framer-motion";
import { Search, Bell, UserPlus, Store, Wrench, Boxes, X, Waves, Package, Building2, Droplets, ChevronDown, Mail, MessageSquare } from "lucide-react";

const BG = "/dash-bg.jpg";
const LOGO = "/aqualux-nexus-logo-flare.png";
const COMPANIES = ["Glamour Pools and Spas", "Just Spas Newcastle"];
const todayISO = () => new Date().toLocaleDateString("en-CA");
const money = (n) => `$${Number(n || 0).toFixed(0)}`;

const Tap = ({ className = "", children, ...p }) => (
  <motion.button whileHover={{ y: -2 }} whileTap={{ scale: 0.97 }} className={className} {...p}>{children}</motion.button>
);

/* top-strip glass dropdown (Company / Employee) */
function TopSelect({ label, value, options, onChange, testid }) {
  return (
    <label className="flex flex-col gap-1 min-w-[220px]">
      <span className="text-[10px] uppercase tracking-[0.22em] text-white/85 font-semibold text-center">{label}</span>
      <div className="relative">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testid}
          className="w-full appearance-none rounded-xl bg-white/92 backdrop-blur px-4 py-2.5 pr-10 font-display font-extrabold text-lg lg:text-xl text-[#123a63] shadow-lg border border-white/60 focus:ring-4 focus:ring-cyan-200 outline-none cursor-pointer text-center"
        >
          {options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
        <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#123a63]/70" />
      </div>
    </label>
  );
}

const SHOWROOMS = [
  { t: "SPA SHOWROOM", i: Waves, a: "tab:spa_search", tid: "sb-spas", smart: true },
  { t: "SAUNA SHOWROOM", i: Building2, a: "tab:sauna_search", tid: "sb-saunas", smart: true },
  { t: "GAZEBO SHOWROOM", i: Store, a: "tab:gazebo_search", tid: "sb-gazebos", smart: true },
  { t: "PARTS WAREHOUSE", i: Package, a: "tab:parts_search", tid: "sb-parts", smart: false },
];

export default function Switchboard({ isAdmin, onNav, onNewClient }) {
  const { user, logout } = useAuth();
  const [company, setCompany] = useState(() => localStorage.getItem("aqualux_company") || COMPANIES[0]);
  const [employee, setEmployee] = useState(() => localStorage.getItem("aqualux_employee") || "");
  const [employees, setEmployees] = useState([]);
  const [s, setS] = useState({ takings: 0, count: 0, reminders: 0 });

  // persist selections so every screen can attribute transactions to the right entity/person
  useEffect(() => { localStorage.setItem("aqualux_company", company); }, [company]);
  useEffect(() => { if (employee) localStorage.setItem("aqualux_employee", employee); }, [employee]);

  useEffect(() => {
    (async () => {
      try {
        const df = `${todayISO()}T00:00:00`, dt = `${todayISO()}T23:59:59`;
        const [rep, rem, clerks] = await Promise.all([
          api("GET", `/reports/sales?date_from=${df}&date_to=${dt}`).catch(() => ({})),
          api("GET", "/reminders").catch(() => []),
          api("GET", "/pos/clerks").catch(() => []),
        ]);
        const names = (clerks || []).map((c) => (typeof c === "string" ? c : (c.name || c.employee || ""))).filter(Boolean);
        const uniq = Array.from(new Set(names));
        setEmployees(uniq);
        if (!employee) setEmployee(user?.name && uniq.includes(user.name) ? user.name : (uniq[0] || user?.name || ""));
        const todayStr = todayISO();
        setS({
          takings: rep.total || 0, count: rep.count || 0,
          reminders: (rem || []).filter((r) => !r.done && (r.due_date || "") <= todayStr).length,
        });
      } catch (e) { console.error("dashboard load", e); }
    })();
  }, []); // eslint-disable-line

  const go = (a) => () => onNav(a);
  const counts = useMailCounts(), mailNew = counts.new, smsNew = counts.sms;
  const shake = useMailShake("email"), shakeSms = useMailShake("sms");
  // Manual + smart prompt: on a showroom sale while trading as Glamour, offer to switch entity
  const goShowroom = (a) => () => {
    if (company === "Glamour Pools and Spas" &&
        window.confirm("Showroom sales belong to Just Spas Newcastle.\n\nSwitch the active company to \u201cJust Spas Newcastle\u201d for this sale?")) {
      setCompany("Just Spas Newcastle");
    }
    onNav(a);
  };

  return (
    <div data-testid="switchboard-panel">
      {/* ============== MOBILE (technician) condensed ============== */}
      <div className="md:hidden flex flex-col gap-3">
        <p className="font-display text-xl font-bold text-slate-900">Field workspace</p>
        {[
          { t: "Today's Reminders", b: "Start here", i: Bell, bg: "bg-amber-500", a: "tab:reminders", tid: "mob-tile-reminders", c: s.reminders },
          { t: "Search Everything", b: "Find fast", i: Search, bg: "bg-slate-800", a: "tab:search", tid: "mob-tile-search" },
          { t: "Today's Work Orders", b: "Service", i: Wrench, bg: "bg-amber-600", a: "tab:service", tid: "mob-tile-work-orders" },
          { t: "Run On-Site Water Test", b: "Mobile probe", i: Droplets, bg: "bg-sky-600", a: "tab:clients", tid: "mob-tile-water-test" },
          { t: "Client Lookup & Pools", b: "CRM", i: UserPlus, bg: "bg-slate-900", a: "tab:clients", tid: "mob-tile-clients" },
          { t: "Field POS / Van Invoice", b: "Tap to pay", i: Store, bg: "bg-emerald-600", a: "tab:pos", tid: "mob-tile-pos" },
          { t: "Barcode / Chemical Scanner", b: "Camera", i: Boxes, bg: "bg-slate-100 !text-slate-800", a: "tab:inventory", tid: "mob-tile-scan" },
        ].map((m) => (
          <button key={m.tid} onClick={go(m.a)} data-testid={m.tid} className={`flex items-center gap-3 rounded-2xl p-4 text-white ${m.bg} active:scale-[0.98] transition-transform`}>
            <m.i className="h-6 w-6 shrink-0" />
            <span className="text-left flex-1"><span className="block font-display font-bold">{m.t}</span><span className="block text-[11px] opacity-80">{m.b}</span></span>
            {m.c > 0 && <span className="min-w-[24px] h-6 px-1.5 rounded-full bg-white/25 text-white text-xs font-bold flex items-center justify-center">{m.c}</span>}
          </button>
        ))}
      </div>

      {/* ============== DESKTOP — clean Dashboard (fits one screen) ============== */}
      <div style={{ height: "calc(100vh - 4.5rem)" }} className="hidden md:flex md:flex-col -m-4 lg:-m-6 relative overflow-hidden">
        <img src={BG} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-sky-950/35 via-sky-900/20 to-cyan-950/45" />

        <div className="relative flex-1 min-h-0 flex flex-col px-6 lg:px-10 py-4">
          {/* ---------- top strip: logo · Company · Employee (all top-aligned) ---------- */}
          <div className="flex flex-wrap items-start justify-between gap-x-8 gap-y-3 mb-3 shrink-0">
            <div className="flex flex-col gap-1">
              <span className="text-[10px] uppercase tracking-[0.22em] font-semibold select-none">&nbsp;</span>
              <div className="inline-flex items-center rounded-xl bg-white/90 shadow-lg px-4 py-2.5" data-testid="dash-logo">
                <img src={LOGO} alt="Aqualux Nexus" className="h-9 w-auto" />
              </div>
            </div>
            <div className="flex flex-wrap items-start gap-4">
              <div className="flex flex-col items-center gap-1.5">
                <TopSelect label="Company" value={company} options={COMPANIES} onChange={setCompany} testid="dash-company-select" />
                <button onClick={go("overlay:company")} data-testid="dash-view-company"
                  className="text-[12px] font-semibold text-white/90 hover:text-white underline underline-offset-2">View Company Details</button>
              </div>
              <TopSelect label="Current Logged-In User"
                value={employee}
                options={employees.length ? employees : [employee || (user?.name || "Clerk")]}
                onChange={setEmployee} testid="dash-employee-select" />
            </div>
          </div>

          {/* ---------- main body: centre stack + right cards ---------- */}
          <div className="flex-1 min-h-0 grid grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)_13rem] gap-6 max-w-[1250px] 2xl:max-w-[1560px] w-full mx-auto" data-testid="sb-columns">
            {/* left: showrooms + parts */}
            <div className="flex flex-col gap-3 min-h-0" data-testid="sb-col-showrooms">
              {SHOWROOMS.map((b) => (
                <Tap key={b.tid} onClick={b.smart ? goShowroom(b.a) : go(b.a)} data-testid={b.tid}
                  className="group relative w-full flex-1 min-h-0 rounded-2xl overflow-hidden border border-white/70 shadow-[0_10px_22px_-8px_rgba(12,74,110,0.55)] bg-gradient-to-b from-white via-sky-200 to-sky-400 flex items-center justify-center gap-3 transition-[filter] hover:brightness-105">
                  <span className="pointer-events-none absolute inset-x-0 top-0 h-1/2 bg-gradient-to-b from-white/85 to-white/0" />
                  <span className="pointer-events-none absolute inset-0 rounded-2xl ring-1 ring-inset ring-white/60" />
                  <b.i className="relative h-7 w-7 shrink-0 text-[#15406b] drop-shadow-[0_1px_0_rgba(255,255,255,0.75)]" />
                  <span className="relative whitespace-nowrap font-display font-extrabold text-lg min-[1500px]:text-2xl 2xl:text-3xl tracking-wide text-[#15406b] drop-shadow-[0_1px_0_rgba(255,255,255,0.8)]">{b.t}</span>
                </Tap>
              ))}
            </div>
            {/* centre: quick icons, new client, POS */}
            <div className="flex flex-col gap-3 min-h-0" data-testid="sb-col-centre">
              <div className="flex items-stretch justify-center gap-3 min-[1500px]:gap-5 shrink-0">
                <Tap onClick={go("overlay:inbox")} data-testid="sb-inbox" title="My Inbox" className="relative h-20 w-20 rounded-full bg-gradient-to-br from-amber-300 to-amber-500 border-4 border-white shadow-xl text-[#3b2a05] flex flex-col items-center justify-center gap-0.5">
                  <Mail className={`h-9 w-9 ${shake ? "mail-shake" : ""}`} strokeWidth={1.8} data-testid="sb-inbox-icon" />
                  {mailNew > 0 && <span data-testid="sb-inbox-count" className="absolute -top-1 -right-1 min-w-[24px] h-6 px-1.5 rounded-full bg-red-600 text-white text-xs font-extrabold border-2 border-white flex items-center justify-center">{mailNew > 999 ? "999+" : mailNew}</span>}
                </Tap>
                <Tap onClick={() => window.dispatchEvent(new CustomEvent("aqualux:open-inbox", { detail: { mailbox: "sms" } }))} data-testid="sb-sms" title="SMS replies" className="relative h-20 w-20 rounded-full bg-gradient-to-br from-emerald-400 to-green-600 border-4 border-white shadow-xl text-white flex flex-col items-center justify-center gap-0.5">
                  <MessageSquare className={`h-7 w-7 ${shakeSms ? "mail-shake" : ""}`} strokeWidth={2} data-testid="sb-sms-icon" /><span className="font-display font-extrabold text-[11px] leading-none">SMS</span>
                  {smsNew > 0 && <span data-testid="sb-sms-count" className="absolute -top-1 -right-1 min-w-[24px] h-6 px-1.5 rounded-full bg-red-600 text-white text-xs font-extrabold border-2 border-white flex items-center justify-center">{smsNew > 999 ? "999+" : smsNew}</span>}
                </Tap>
                <Tap onClick={go("tab:search")} data-testid="sb-search" className="h-20 w-20 rounded-full bg-gradient-to-br from-sky-500 to-blue-700 border-4 border-white shadow-xl text-white flex flex-col items-center justify-center gap-0.5">
                  <Search className="h-6 w-6" /><span className="font-display font-extrabold text-sm">Search</span>
                </Tap>
                <Tap onClick={go("tab:reminders")} data-testid="sb-reminder" className="relative h-20 w-20 rounded-full bg-gradient-to-br from-rose-500 to-red-700 border-4 border-white shadow-xl text-white flex flex-col items-center justify-center gap-0.5">
                  <Bell className="h-6 w-6" /><span className="font-display font-extrabold text-[10px] leading-none">REMINDER</span>
                  {s.reminders > 0 && <span className="absolute -top-1 -right-1 min-w-[24px] h-6 px-1.5 rounded-full bg-white text-rose-700 text-xs font-extrabold border-2 border-rose-600 flex items-center justify-center">{s.reminders}</span>}
                </Tap>
                <Tap onClick={logout} data-testid="sb-close" className="h-20 w-20 rounded-2xl bg-slate-900 border-4 border-white shadow-xl text-white flex flex-col items-center justify-center gap-0.5">
                  <X className="h-6 w-6" /><span className="font-display font-bold text-[10px] leading-none text-center">CLOSE<br/>DATABASE</span>
                </Tap>
              </div>

              <Tap onClick={onNewClient} data-testid="sb-new-client" className="w-full rounded-2xl bg-gradient-to-br from-green-500 to-emerald-600 border-4 border-white shadow-xl text-white py-3.5 flex items-center justify-center gap-3 shrink-0">
                <UserPlus className="h-7 w-7" /><span className="font-display font-extrabold text-2xl">+ New Client</span>
              </Tap>

              <Tap onClick={go("tab:pos")} data-testid="sb-pos"
                className="group relative w-full flex-1 min-h-0 rounded-2xl overflow-hidden border border-white/70 shadow-[0_12px_26px_-8px_rgba(12,74,110,0.6)]">
                <img src="/pos-counter-sale.png" alt="POS Counter Sales" className="absolute inset-0 h-full w-full object-cover" />
                <span className="pointer-events-none absolute inset-0 bg-white/25 group-hover:bg-white/10 transition-colors" />
                <span className="relative h-full w-full flex flex-col items-center justify-center text-center leading-none gap-1">
                  <span className="font-display font-extrabold text-3xl lg:text-5xl tracking-wide text-slate-700 drop-shadow-[0_2px_3px_rgba(255,255,255,0.95)]">POS</span>
                  <span className="font-display font-extrabold text-2xl lg:text-4xl tracking-wide text-slate-700 drop-shadow-[0_2px_3px_rgba(255,255,255,0.95)]">COUNTER SALES</span>
                  {s.count > 0 && <span className="mt-1 text-xs font-bold text-slate-700 bg-white/75 rounded-full px-2.5 py-0.5">{s.count} today · {money(s.takings)}</span>}
                </span>
              </Tap>
            </div>

            {/* right cards: Supplier's Folders · Supplier Contacts (centred to the tiles stack) */}
            <div className="flex flex-col justify-center gap-5 min-h-0" data-testid="sb-col-suppliers">
              <Tap onClick={go("overlay:suppliers_folders")} data-testid="sb-docs"
                className="rounded-3xl bg-white/15 backdrop-blur-md border border-white/40 shadow-2xl p-5 flex flex-col items-center gap-2 text-center">
                <img src="/icon-company-docs.png" alt="" className="h-24 w-24 object-contain drop-shadow" />
                <span className="font-display font-extrabold text-lg text-white drop-shadow leading-tight">Supplier's<br/>Folders</span>
              </Tap>
              <Tap onClick={go("overlay:business_contacts")} data-testid="sb-suppliers"
                className="rounded-3xl bg-white/15 backdrop-blur-md border border-white/40 shadow-2xl p-5 flex flex-col items-center gap-2 text-center">
                <img src="/icon-supplier.webp" alt="" className="h-24 w-24 object-contain drop-shadow" />
                <span className="font-display font-extrabold text-lg text-white drop-shadow leading-tight">Supplier<br/>Contacts</span>
              </Tap>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
